import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, Package, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInventory() {
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStock, setEditingStock] = useState({});

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inventory`);
      setProducts(res.data.products);
      setLowStock(res.data.low_stock);
    } catch (err) {
      console.error('Error fetching inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStock = async (productId) => {
    const newQty = editingStock[productId];
    if (newQty === undefined) return;
    
    try {
      await axios.put(`${API}/admin/inventory/${productId}?stock_qty=${newQty}`);
      toast.success('Stock updated');
      fetchInventory();
      setEditingStock(prev => {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      });
    } catch (err) {
      toast.error('Failed to update stock');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-inventory">
        <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-8">
          Inventory
        </h1>

        {/* Low Stock Alert */}
        {lowStock.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-8">
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <h2 className="text-red-500 font-bold">Low Stock Alert</h2>
            </div>
            <p className="text-red-400 text-sm">
              {lowStock.length} product(s) are running low on stock:
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {lowStock.slice(0, 5).map(p => (
                <Badge key={p.id} className="bg-red-500/20 text-red-500">
                  {p.name} ({p.stock_qty})
                </Badge>
              ))}
              {lowStock.length > 5 && (
                <Badge className="bg-red-500/20 text-red-500">
                  +{lowStock.length - 5} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Inventory Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Current Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Low Threshold</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Update</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-4">
                      <div className="h-8 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    No products found
                  </td>
                </tr>
              ) : (
                products.map(product => {
                  const isLow = product.stock_qty <= product.low_stock_threshold;
                  return (
                    <tr key={product.id} className={`hover:bg-zinc-900/50 ${isLow ? 'bg-red-500/5' : ''}`}>
                      <td className="px-4 py-4">
                        <p className="text-white font-medium">{product.name}</p>
                      </td>
                      <td className="px-4 py-4 text-zinc-400 font-mono text-sm">{product.sku}</td>
                      <td className="px-4 py-4">
                        <span className={`font-mono font-bold ${isLow ? 'text-red-500' : 'text-white'}`}>
                          {product.stock_qty}
                        </span>
                        {product.track_serials && (
                          <span className="ml-2 text-xs text-[#78BE20] flex items-center gap-1 inline-flex">
                            <Hash className="h-3 w-3" />
                            {product.serial_stock !== undefined ? `${product.serial_stock} serialized` : 'tracked'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-zinc-400 font-mono">
                        {product.low_stock_threshold}
                      </td>
                      <td className="px-4 py-4">
                        {isLow ? (
                          <Badge className="bg-red-500/20 text-red-500">Low Stock</Badge>
                        ) : product.stock_qty === 0 ? (
                          <Badge className="bg-zinc-700 text-zinc-400">Out of Stock</Badge>
                        ) : (
                          <Badge className="bg-[#78BE20]/20 text-[#78BE20]">In Stock</Badge>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            value={editingStock[product.id] ?? product.stock_qty}
                            onChange={(e) => setEditingStock(prev => ({ ...prev, [product.id]: parseInt(e.target.value) || 0 }))}
                            className="w-24 bg-zinc-800 border-zinc-700 text-right"
                          />
                          <Button
                            size="sm"
                            onClick={() => updateStock(product.id)}
                            disabled={editingStock[product.id] === undefined}
                            className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                          >
                            Update
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
